import React, { useEffect, useState } from 'react';
import Chip from '@material-ui/core/Chip';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import TeethChart from '../../components/TeethChart';
import colors from './colors';
import selectionRange from './selectionRange';

const useStyles = makeStyles(theme => ({
  tooth: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: 'smaller',
    color: theme.palette.primary.light
  },
  upperTooth: {
    top: '-12px'
  },
  lowerTooth: {
    bottom: '-12px'
  }
}));

const trimName = name => {
  const maxLength = 30;
  if (name.length <= maxLength) {
    return name;
  }
  return `${name.substring(0, maxLength - 3)}...`;
};

const Teeth = ({
  data,
  size,
  onBracketLibraryClick,
  onTeethSelection,
  toothPropsGenerator,
  BracketListProps
}) => {
  const classes = useStyles();
  const [bracketColor, setBracketColor] = useState({});
  const [isCtrlPressed, setIsCtrlPressed] = React.useState(false);
  const [firstSelectedTooth, setFirstSelectedTooth] = React.useState(null);

  const bracketsMap = data.reduce((acc, tooth) => {
    // eslint-disable-next-line no-param-reassign
    acc[tooth.bracket_library_id] = tooth.bracket_library_name;
    return acc;
  }, {});

  const selectedBrackets = Object.keys(bracketsMap).map(id => ({
    id: Number(id),
    name: bracketsMap[id]
  }));

  useEffect(() => {
    const currentBrackets = Object.keys(bracketColor)
      .map(bracket => Number(bracket))
      .sort();
    const selectedBracketIds = [...new Set(data.map(item => item.bracket_library_id))].sort();

    // check if equals
    if (JSON.stringify(selectedBracketIds) === JSON.stringify(currentBrackets)) {
      return;
    }

    const newBracketColor = {};
    const newBrackets = [];
    selectedBracketIds.forEach(bracket => {
      if (bracketColor[bracket]) {
        newBracketColor[bracket] = bracketColor[bracket];
      } else {
        newBrackets.push(bracket);
      }
    });

    const currentColors = Object.values(bracketColor);
    const unassignedColors = colors.filter(color => !currentColors.includes(color));
    newBrackets.forEach(bracket => {
      newBracketColor[bracket] = unassignedColors.shift();
    });
    setBracketColor(newBracketColor);
  }, [data, bracketColor, setBracketColor]);

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setIsCtrlPressed(true);
      }
    };

    const handleKeyUp = e => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setIsCtrlPressed(false);
        setFirstSelectedTooth(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const toothMapping = data.reduce((acc, item) => {
    // eslint-disable-next-line no-param-reassign
    acc[item.tooth_number] = item.bracket_library_id;
    return acc;
  }, {});

  const chipStyle = {};

  if (size === 'small') {
    chipStyle.height = '16px';
    chipStyle.fontSize = '10px';
  }

  const handleToothSelection = tooth => {
    if (!onTeethSelection) {
      return;
    }
    if (isCtrlPressed && firstSelectedTooth) {
      setFirstSelectedTooth(null);
      const teethRange = selectionRange(firstSelectedTooth, tooth);
      onTeethSelection(teethRange);
    } else if (isCtrlPressed) {
      setFirstSelectedTooth(tooth);
    } else {
      onTeethSelection([tooth]);
    }
  };

  return (
    <div className="custom-bracket-set-teeth">
      <TeethChart
        hideSectionLabels
        size={size}
        toothPropsGenerator={(name, props) => {
          const commonToothProps = {
            ...props,
            ...(toothPropsGenerator ? toothPropsGenerator(name, props) : {}),
            onChange: () => handleToothSelection(name),
            customRender: original => {
              if (size === 'small') {
                return original;
              }
              const isUpperTooth = name.startsWith('U');
              const toothData = data.find(item => item.tooth_number === name);
              return (
                <div style={{ position: 'relative' }}>
                  <span
                    className={`${classes.tooth} ${
                      isUpperTooth ? classes.upperTooth : classes.lowerTooth
                    }`}
                  >
                    {(toothData && toothData.height) || ''}
                  </span>
                  {original}
                </div>
              );
            }
          };
          if (bracketColor[toothMapping[name]]) {
            return {
              ...commonToothProps,
              color: bracketColor[toothMapping[name]],
              fontColor: 'white'
            }; // Example of overriding color for UR1
          }
          return { ...commonToothProps, color: '#ffffff', fontColor: '#000000' }; // Default color if not set
        }}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          marginTop: size === 'small' ? 0 : 16
        }}
        {...BracketListProps}
      >
        {selectedBrackets.map(bracket => {
          const color = bracketColor[bracket.id] || '#000000'; // Default color if not set

          return (
            <Chip
              key={bracket.id}
              size="small"
              label={trimName(bracket.name)}
              title={bracket.name}
              onClick={onBracketLibraryClick && (() => onBracketLibraryClick(bracket))}
              style={{
                backgroundColor: color,
                color: 'white',
                ...chipStyle,
                marginRight: 8,
                marginTop: 8
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

Teeth.propTypes = {
  size: PropTypes.string,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      tooth_number: PropTypes.string.isRequired,
      bracket_library_id: PropTypes.string.isRequired,
      bracket_library_name: PropTypes.string.isRequired
    })
  ),
  onBracketLibraryClick: PropTypes.func,
  toothPropsGenerator: PropTypes.func,
  onTeethSelection: PropTypes.func,
  BracketListProps: PropTypes.object
};

Teeth.defaultProps = {
  size: 'large',
  data: [],
  onBracketLibraryClick: null,
  toothPropsGenerator: null,
  onTeethSelection: null,
  BracketListProps: {}
};

export default Teeth;
